// SETTINGS V2 — collapsed terminal guest-pass group (GP-COLLAPSE §7.5).
//
// One full-width toggler row ("Ukončené (N)") that reveals the expired/revoked
// rows the caller hands it, plus the display-window hint at the group's end.
//
// §4.2 invariants this component upholds:
//  • It renders ONLY what the caller passes as children — and the caller feeds it
//    exclusively `partitionGuestPasses(...).terminal` (see guestPassPartition.ts,
//    where the Active-can-never-be-hidden guarantee actually lives). This
//    component has no filter of its own and no access to the full list, so it
//    cannot conceal a row the partition placed in the active group.
//  • The expanded flag is transient local state — `useState(false)`, reset on
//    unmount, NEVER persisted and never written to Redux/localStorage. There is
//    no per-row hide affordance anywhere.
//
// Inline styles + scoped CSS-var tokens + the shared `.btn` atom. No
// MUI/sx/dt/Tailwind. SEC-REQ-2: no side effects.
import React, { useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";

export interface GuestPassTerminalGroupV2Props {
  /** Number of terminal rows in the response — what the toggler counts. */
  count: number;
  /** The already-partitioned terminal rows, rendered only while expanded. */
  children: React.ReactNode;
}

const GuestPassTerminalGroupV2: React.FC<GuestPassTerminalGroupV2Props> = ({
  count,
  children,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();
  const [expanded, setExpanded] = useState(false);

  if (count <= 0) return null;

  return (
    <div style={{ width: "100%" }}>
      <button
        type="button"
        className="btn btn-ghost"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%",
          height: isPhone ? 44 : 36,
          padding: isPhone ? "0 13px" : "0 16px",
          borderRadius: 0,
          borderTop: "1px solid var(--border-soft)",
          justifyContent: "space-between",
          fontSize: 12.5,
          color: "var(--muted)",
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {t("guestPass.terminalToggle", { count }) as string}
        </span>
        <span aria-hidden="true" style={{ flexShrink: 0, fontSize: 11 }}>
          {expanded ? "▴" : "▾"}
        </span>
      </button>

      {expanded && (
        <div style={{ width: "100%" }}>
          {children}
          <div
            style={{
              padding: isPhone ? "9px 13px 11px" : "9px 16px 11px",
              borderTop: "1px solid var(--border-soft)",
              fontSize: 11.5,
              lineHeight: 1.55,
              color: "var(--faint)",
            }}
          >
            {t("guestPass.terminalWindowHint") as string}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestPassTerminalGroupV2;
